import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Stack,
  Select,
  Pagination,
  Modal,
  Skeleton,
  Code,
  ActionIcon,
  Tooltip,
  Avatar,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconDownload,
  IconSettings,
  IconUser,
  IconShield,
  IconTarget,
  IconSpeakerphone,
  IconCash,
  IconLogin,
  IconActivity,
  IconEye,
  IconFileCertificate,
  IconMail,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import axios from "axios";
import { ExportModal } from "../../../components/ExportModal";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import { usePerformRequest } from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";

type AuditLog = {
  id: string;
  action: string;
  actorAdminId?: string;
  actorEmail?: string;
  ip?: string;
  userAgent?: string;
  entityType?: string;
  entityId?: string;
  status: "success" | "failure";
  message?: string;
  metadata?: any;
  createdAt: string;
};

const getActionIcon = (action: string) => {
  const lower = action.toLowerCase();
  if (lower.includes("login") || lower.includes("logout"))
    return <IconLogin size={16} />;
  if (lower.includes("config")) return <IconSettings size={16} />;
  if (lower.includes("admin")) return <IconShield size={16} />;
  if (lower.includes("user") || lower.includes("waitlist"))
    return <IconUser size={16} />;
  if (lower.includes("mission")) return <IconTarget size={16} />;
  if (lower.includes("campaign")) return <IconSpeakerphone size={16} />;
  if (lower.includes("withdrawal")) return <IconCash size={16} />;
  if (lower.includes("proof")) return <IconFileCertificate size={16} />;
  if (lower.includes("invite")) return <IconMail size={16} />;
  if (lower.includes("file") || lower.includes("upload"))
    return <IconUpload size={16} />;
  return <IconActivity size={16} />;
};

const getActionColor = (action: string) => {
  const lower = action.toLowerCase();
  if (
    lower.includes("delete") ||
    lower.includes("revoke") ||
    lower.includes("reject")
  )
    return "red";
  if (
    lower.includes("create") ||
    lower.includes("approve") ||
    lower.includes("activate")
  )
    return "green";
  if (lower.includes("update") || lower.includes("suspend")) return "green";
  return "gray";
};

const AuditLogs = () => {
  useEffect(() => {
    document.title = "Audit Logs | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null);
  const [adminFilter, setAdminFilter] = useState<string | null>(null);
  const [adminOptions, setAdminOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const endpoint = useMemo(
    () =>
      Endpoints.GetAuditLogs({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        actorAdminId: adminFilter || undefined,
        from: dateRange[0] ? new Date(dateRange[0]).toISOString() : undefined,
        to: dateRange[1] ? new Date(dateRange[1]).toISOString() : undefined,
      }),
    [
      currentPage,
      pageSize,
      debouncedSearch,
      actionFilter,
      entityTypeFilter,
      adminFilter,
      dateRange,
    ],
  );

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem("t");
        const endpoint = Endpoints.GetAdmins({ limit: 100 });
        const response = await axios.get(endpoint.route, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const admins = response.data.data.map((admin: any) => ({
          value: admin.id,
          label: `${admin.firstName} ${admin.lastName}`,
        }));
        setAdminOptions(admins);
      } catch (error) {
        console.error("Failed to fetch admins", error);
      }
    };
    fetchAdmins();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<AuditLog[]>
  >({
    method: endpoint.method,
    route: endpoint.route,
    showError: true,
  });

  const logs = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("t");
      const endpoint = Endpoints.GetAuditReport({
        format: "csv",
        action: actionFilter || undefined,
        entity: entityTypeFilter || undefined,
        startDate: dateRange[0]
          ? new Date(dateRange[0]).toISOString()
          : undefined,
        endDate: dateRange[1]
          ? new Date(dateRange[1]).toISOString()
          : undefined,
      });

      const response = await axios.get(endpoint.route, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `audit_logs_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Export failed", e);
      setErrorMessage("Failed to export audit logs");
      setErrorModalOpen(true);
    }
  };

  const skeletonRows = Array.from({ length: 10 }).map((_, idx) => (
    <Table.Tr key={`skel-${idx}`}>
      <Table.Td>
        <Skeleton height={14} width={120} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={100} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={150} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={100} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={30} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard
      permission={AdminPermissions.VIEW_AUDIT_LOGS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="audit-page">
        <Container size="xl">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={1}>Audit Logs</Title>
                <Text size="sm" c="dimmed" mt={4}>
                  Track all administrative actions
                </Text>
              </div>
              <Group>
                <Button
                  leftSection={<IconDownload size={16} />}
                  variant="outline"
                  onClick={() => setExportModalOpen(true)}
                  disabled={logs.length === 0}
                >
                  Export CSV
                </Button>
                <Button
                  leftSection={<IconRefresh size={16} />}
                  variant="light"
                  onClick={() => reloadData()}
                  loading={isLoading}
                >
                  Refresh
                </Button>
              </Group>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" align="flex-end">
                <TextInput
                  placeholder="Search logs..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  label="Search"
                />
                <Select
                  placeholder="Action Type"
                  label="Action"
                  data={[
                    { value: "ADMIN_LOGIN", label: "Admin Login" },
                    {
                      value: "ADMIN_LOGIN_FAILED",
                      label: "Admin Login Failed",
                    },
                    { value: "ADMIN_LOGOUT", label: "Admin Logout" },
                    {
                      value: "ADMIN_ONBOARD_COMPLETE",
                      label: "Admin Onboard Complete",
                    },
                    { value: "USER_UPDATE", label: "User Updated" },
                    { value: "USER_ACTIVATE", label: "User Activated" },
                    { value: "USER_SUSPEND", label: "User Suspended" },
                    { value: "USER_DELETE", label: "User Deleted" },
                    { value: "USER_EXPORT", label: "User Export" },
                    { value: "MISSION_CREATE", label: "Activity Created" },
                    { value: "MISSION_UPDATE", label: "Activity Updated" },
                    {
                      value: "MISSION_DUPLICATE",
                      label: "Activity Duplicated",
                    },
                    { value: "MISSION_DELETE", label: "Activity Deleted" },
                    { value: "MISSION_EXPORT", label: "Activity Export" },
                    { value: "CAMPAIGN_CREATE", label: "Campaign Created" },
                    { value: "CAMPAIGN_UPDATE", label: "Campaign Updated" },
                    { value: "CAMPAIGN_DELETE", label: "Campaign Deleted" },
                    { value: "CAMPAIGN_EXPORT", label: "Campaign Export" },
                    {
                      value: "WITHDRAWAL_APPROVE",
                      label: "Withdrawal Approved",
                    },
                    {
                      value: "WITHDRAWAL_REJECT",
                      label: "Withdrawal Rejected",
                    },
                    { value: "WITHDRAWAL_EXPORT", label: "Withdrawal Export" },
                    { value: "PROOF_EXPORT", label: "Proof Export" },
                    { value: "CONFIG_CREATE", label: "Config Created" },
                    { value: "CONFIG_UPDATE", label: "Config Updated" },
                    { value: "CONFIG_DELETE", label: "Config Deleted" },
                    { value: "AUDIT_LOG_EXPORT", label: "Audit Log Export" },
                    {
                      value: "ACTIVITY_EXPORT",
                      label: "Activity Report Export",
                    },
                  ]}
                  value={actionFilter}
                  onChange={setActionFilter}
                  clearable
                  searchable
                  style={{ width: 180 }}
                />
                <Select
                  placeholder="Entity Type"
                  label="Entity"
                  data={[
                    { value: "Admin", label: "Admin" },
                    { value: "User", label: "User" },
                    { value: "Campaign", label: "Campaign" },
                    { value: "Mission", label: "Activity" },
                    { value: "Proof", label: "Proof" },
                    { value: "Withdrawal", label: "Withdrawal" },
                    { value: "Config", label: "Config" },
                    { value: "Waitlist", label: "Waitlist" },
                    { value: "Invite", label: "Invite" },
                    { value: "AuditLog", label: "Audit Log" },
                    { value: "Activity", label: "Activity Item" },
                  ]}
                  value={entityTypeFilter}
                  onChange={setEntityTypeFilter}
                  clearable
                  style={{ width: 150 }}
                />
                <Select
                  placeholder="Admin"
                  label="Admin"
                  data={adminOptions}
                  value={adminFilter}
                  onChange={setAdminFilter}
                  clearable
                  searchable
                  style={{ width: 180 }}
                />
                <DatePickerInput
                  type="range"
                  label="Date Range"
                  placeholder="Pick dates"
                  value={dateRange}
                  onChange={(value) =>
                    setDateRange(value as [Date | null, Date | null])
                  }
                  clearable
                  style={{ width: 220 }}
                />
              </Group>

              <div style={{ overflowX: "auto" }}>
                <Table striped highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Admin</Table.Th>
                      <Table.Th>Entity</Table.Th>
                      <Table.Th>Target ID</Table.Th>
                      <Table.Th>IP Address</Table.Th>
                      <Table.Th>Details</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : logs.length > 0 ? (
                      logs.map((log) => (
                        <Table.Tr key={log.id}>
                          <Table.Td>
                            <Text size="sm">
                              {new Date(log.createdAt).toLocaleString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              leftSection={getActionIcon(log.action)}
                              color={getActionColor(log.action)}
                              variant="light"
                            >
                              {log.action}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              <Avatar size="sm" radius="xl" color="green">
                                {(log.actorEmail || "S")
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                              <Text size="sm">
                                {log.actorEmail || "System"}
                              </Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{log.entityType || "-"}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Tooltip label={log.entityId} openDelay={500}>
                              <Text
                                size="sm"
                                style={{
                                  fontFamily: "monospace",
                                  maxWidth: 100,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {log.entityId || "-"}
                              </Text>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{ fontFamily: "monospace" }}
                            >
                              {log.ip || "-"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              variant="subtle"
                              onClick={() => setSelectedLog(log)}
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={7} style={{ textAlign: "center" }}>
                          <Text c="dimmed" py="xl">
                            No audit logs found
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}

              <Group justify="space-between" mt="md">
                <Text size="xs" c="dimmed">
                  {totalRecords} records found
                </Text>
                <Select
                  data={["10", "20", "50", "100"]}
                  value={String(pageSize)}
                  onChange={(v) => {
                    setPageSize(Number(v) || 20);
                    setCurrentPage(1);
                  }}
                  size="xs"
                  w={80}
                />
              </Group>
            </Paper>
          </Stack>
        </Container>

        <Modal
          opened={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title={
            <Group gap="xs">
              <IconActivity size={20} />
              <Text fw={700}>Audit Log Details</Text>
            </Group>
          }
          size="lg"
          centered
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
        >
          {selectedLog && (
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Badge
                  size="lg"
                  variant="filled"
                  color={getActionColor(selectedLog.action)}
                  leftSection={getActionIcon(selectedLog.action)}
                >
                  {selectedLog.action}
                </Badge>
                <Text size="sm" c="dimmed">
                  {new Date(selectedLog.createdAt).toLocaleString()}
                </Text>
              </Group>

              <Paper withBorder p="md" radius="md" bg="var(--bg-surface-2)">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Actor
                    </Text>
                    <Group gap="xs">
                      <Avatar size="sm" radius="xl" color="green">
                        {(selectedLog.actorEmail || "S")
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>
                      <Text size="sm" fw={500}>
                        {selectedLog.actorEmail || "System"}
                      </Text>
                    </Group>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      IP Address
                    </Text>
                    <Code>{selectedLog.ip || "Unknown"}</Code>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Entity Type
                    </Text>
                    <Badge variant="outline" color="gray">
                      {selectedLog.entityType || "N/A"}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Target ID
                    </Text>
                    <Code>{selectedLog.entityId || "N/A"}</Code>
                  </Group>
                </Stack>
              </Paper>

              <div>
                <Text size="sm" fw={600} mb={4}>
                  Message
                </Text>
                <Paper p="sm" withBorder radius="md">
                  <Text size="sm">
                    {selectedLog.message || "No message provided"}
                  </Text>
                </Paper>
              </div>

              {selectedLog.metadata && (
                <div>
                  <Text size="sm" fw={600} mb={4}>
                    Metadata
                  </Text>
                  <Paper
                    p="xs"
                    withBorder
                    radius="md"
                    bg="dark.7"
                    style={{ overflow: "auto", maxHeight: 200 }}
                  >
                    <Code
                      block
                      style={{ background: "transparent", color: "#f8f9fa" }}
                    >
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </Code>
                  </Paper>
                </div>
              )}
            </Stack>
          )}
        </Modal>

        <ExportModal
          opened={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          title="Export Audit Logs"
          type="audit"
          additionalFilters={
            <Group>
              <Select
                placeholder="Action Type"
                label="Action"
                data={[
                  { value: "ADMIN_LOGIN", label: "Admin Login" },
                  { value: "ADMIN_LOGIN_FAILED", label: "Admin Login Failed" },
                  { value: "ADMIN_LOGOUT", label: "Admin Logout" },
                  { value: "USER_UPDATE", label: "User Updated" },
                  { value: "USER_SUSPEND", label: "User Suspended" },
                  { value: "USER_ACTIVATE", label: "User Activated" },
                  { value: "USER_DELETE", label: "User Deleted" },
                  { value: "MISSION_CREATE", label: "Activity Created" },
                  { value: "MISSION_UPDATE", label: "Activity Updated" },
                  { value: "MISSION_DUPLICATE", label: "Activity Duplicated" },
                  { value: "CAMPAIGN_CREATE", label: "Campaign Created" },
                  { value: "CAMPAIGN_UPDATE", label: "Campaign Updated" },
                  { value: "WITHDRAWAL_APPROVE", label: "Withdrawal Approved" },
                  { value: "WITHDRAWAL_REJECT", label: "Withdrawal Rejected" },
                  { value: "CONFIG_UPDATE", label: "Config Updated" },
                ]}
                value={actionFilter}
                onChange={setActionFilter}
                clearable
                searchable
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Entity Type"
                label="Entity"
                data={[
                  { value: "Admin", label: "Admin" },
                  { value: "User", label: "User" },
                  { value: "Campaign", label: "Campaign" },
                  { value: "Mission", label: "Activity" },
                  { value: "Proof", label: "Proof" },
                  { value: "Withdrawal", label: "Withdrawal" },
                  { value: "Config", label: "Config" },
                  { value: "Waitlist", label: "Waitlist" },
                  { value: "Invite", label: "Invite" },
                ]}
                value={entityTypeFilter}
                onChange={setEntityTypeFilter}
                clearable
                style={{ flex: 1 }}
              />
            </Group>
          }
          onExport={async (dateFilters) => {
            const token = localStorage.getItem("t");
            const endpoint = Endpoints.GetAuditReport({
              format: "csv",
              action: actionFilter || undefined,
              entity: entityTypeFilter || undefined,
              ...dateFilters,
            });

            const response = await axios.get(endpoint.route, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `audit_logs_${new Date().toISOString()}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />
      </div>

      {/* Error Modal */}
      <Modal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={
          <Group gap="xs">
            <IconX size={20} color="red" />
            <Text fw={700} c="red">
              Error
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{errorMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </PermissionGuard>
  );
};

export default AuditLogs;
